import {
  Body,
  Card,
  VBadge,
  VButton,
  VDialog,
  VDropdownMenu,
  VDropdownMenuContent,
  VDropdownMenuItem,
  VDropdownMenuTrigger,
} from '@vrzl/ui';
import { MoreHorizontal, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import {
  useAddUserPerspective,
  useDeleteUser,
  useRemoveUserPerspective,
  useUpdateUserRole,
} from '../api/mutations';
import type { AdminUserItem, PerspectiveOption, Role } from '../api/types';

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'USER', label: 'Gebruiker' },
  { value: 'ADMIN', label: 'Beheerder' },
];

const roleSelectClassName =
  'rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-primary-600';

function initialsFromEmail(email: string): string {
  return email.slice(0, 2).toLowerCase() || '??';
}

export function UserCard({
  user,
  allPerspectives,
}: {
  user: AdminUserItem;
  allPerspectives: PerspectiveOption[];
}) {
  const updateRole = useUpdateUserRole();
  const deleteUser = useDeleteUser();
  const addPerspective = useAddUserPerspective();
  const removePerspective = useRemoveUserPerspective();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const assignedIds = new Set(user.perspectives.map((p) => p.id));
  const available = allPerspectives.filter((p) => !assignedIds.has(p.id));

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-neutral-200">
            <Body as="span" weight="semibold">
              {initialsFromEmail(user.email)}
            </Body>
          </div>
          <Body weight="medium" className="truncate">
            {user.email}
          </Body>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <select
            aria-label={`Rol voor ${user.email}`}
            className={roleSelectClassName}
            value={user.role}
            disabled={updateRole.isPending}
            onChange={(event) =>
              updateRole.mutate({
                userId: user.userId,
                role: event.target.value as Role,
              })
            }
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <VDropdownMenu>
            <VDropdownMenuTrigger asChild>
              <VButton
                type="button"
                variant="ghost"
                size="sm"
                icon={MoreHorizontal}
                aria-label={`Acties voor ${user.email}`}
              />
            </VDropdownMenuTrigger>
            <VDropdownMenuContent align="end" className="min-w-40">
              <VDropdownMenuItem onSelect={() => setConfirmOpen(true)}>
                <Trash2 className="size-4" />
                <span>Verwijderen</span>
              </VDropdownMenuItem>
            </VDropdownMenuContent>
          </VDropdownMenu>
        </div>
      </div>

      <div className="mt-4 border-t border-border pt-4">
        <div className="flex flex-wrap items-center gap-2">
          {user.perspectives.map((perspective) => (
            <VBadge
              key={perspective.id}
              shape="pill"
              color="neutral"
              className="gap-1 pr-1"
            >
              {perspective.name}
              <button
                type="button"
                aria-label={`Verwijder ${perspective.name}`}
                disabled={removePerspective.isPending}
                onClick={() =>
                  removePerspective.mutate({
                    userId: user.userId,
                    perspectiveId: perspective.id,
                  })
                }
                className="rounded-full p-0.5 hover:bg-neutral-300/50"
              >
                <X className="size-3" />
              </button>
            </VBadge>
          ))}

          {available.length === 0 ? (
            <button
              type="button"
              disabled
              className="inline-flex cursor-not-allowed items-center gap-1 rounded-full border border-dashed border-border px-3 py-1 text-sm text-primary-600 opacity-50"
            >
              <Plus className="size-3.5" />
              Perspectief
            </button>
          ) : (
            <VDropdownMenu>
              <VDropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={addPerspective.isPending}
                  className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-3 py-1 text-sm text-primary-600 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="size-3.5" />
                  Perspectief
                </button>
              </VDropdownMenuTrigger>
              <VDropdownMenuContent align="start" className="min-w-44">
                {available.map((perspective) => (
                  <VDropdownMenuItem
                    key={perspective.id}
                    onSelect={() =>
                      addPerspective.mutate({
                        userId: user.userId,
                        perspectiveId: perspective.id,
                      })
                    }
                  >
                    {perspective.name}
                  </VDropdownMenuItem>
                ))}
              </VDropdownMenuContent>
            </VDropdownMenu>
          )}
        </div>
      </div>

      <VDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        size="sm"
        title="Gebruiker verwijderen"
        description="Bevestig het verwijderen van deze gebruiker."
        descriptionHidden
        footer={
          <div className="flex justify-end gap-2">
            <VButton
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
            >
              Annuleren
            </VButton>
            <VButton
              type="button"
              variant="destructive"
              disabled={deleteUser.isPending}
              onClick={() =>
                deleteUser.mutate(user.userId, {
                  onSuccess: () => setConfirmOpen(false),
                })
              }
            >
              {deleteUser.isPending ? 'Verwijderen…' : 'Verwijderen'}
            </VButton>
          </div>
        }
      >
        <div className="px-4 py-3">
          <Body>
            Weet je zeker dat je <span className="font-medium">{user.email}</span>{' '}
            wilt verwijderen? De gebruiker verliest toegang tot deze omgeving.
          </Body>
        </div>
      </VDialog>
    </Card>
  );
}
